import React from 'react';
import {useTranslation} from 'react-i18next';
import {v4 as uuid} from 'uuid';
import {IValidationErrorObject} from '../../../../../incase-domain/common/validationErrorResponse';
import Button from '../../../../../incase-domain/components/button/Button';
import Label from '../../../../../incase-domain/components/label/Label';
import {IElementQuestionMatchPair, IMatchPairQuestion} from '../../../../../incase-domain/elements/question/questionMatchPair';
import {MAX_MATCH_PAIR_LENGTH} from '../../../../../incase-domain/validators/elements/constant';
import {pairElementValidator} from '../../../../../incase-domain/validators/elements/matchPairQuestion';
import AccordionItem from '../../../../../ui/components/AccordionItem';
import {isDeleteButtonDisabled, removeArrayElement, swapArrayElement} from '../../../../../utils/utils';
import {getErrorClasses} from '../../../../../utils/validation';
import DebouncedTextInput from '../../common/debounced/DebouncedTextInput';

interface HighlightedMatchPairElementProps {
    highlightedElement: IElementQuestionMatchPair;
    errors: IValidationErrorObject | null;
    highlightedElementChangeHandler: {
        single: (field: keyof IElementQuestionMatchPair | string, value: unknown) => void;
        multiple: (fields: (keyof IElementQuestionMatchPair)[], values: unknown[]) => void;
    };
}

function HighlightedPairsElement(props: HighlightedMatchPairElementProps): JSX.Element {
    const {highlightedElement, errors, highlightedElementChangeHandler} = props;
    const {t} = useTranslation();

    const handleNewRightElement = (
        elementToChange: IMatchPairQuestion[],
        index: number,
        changedAnswer: IMatchPairQuestion,
        value: string
    ): IMatchPairQuestion => {
        const originalAnswer = elementToChange[index];
        const originalTextCount = highlightedElement.rightElement.filter((item) => item.text === originalAnswer.text).length;
        const newTextCount = highlightedElement.rightElement.filter((item) => item.text === value).length;

        if (originalTextCount === 1 && newTextCount === 0) {
            return changedAnswer;
        }
        return {...changedAnswer, id: uuid()};
    };

    const applyNewRightElement = (
        elementToChange: IMatchPairQuestion[],
        updatedChangedAnswer: IMatchPairQuestion,
        currentRightElementId?: string,
        isUsedElsewhere?: boolean
    ): IMatchPairQuestion[] => {
        if (!isUsedElsewhere) {
            return elementToChange.map((item) => (item.id === currentRightElementId ? updatedChangedAnswer : item));
        }
        return [...elementToChange, updatedChangedAnswer];
    };

    const updateRightElement = (
        elementToChange: IMatchPairQuestion[],
        index: number,
        changedAnswer: IMatchPairQuestion,
        value: string,
        leftElementId: string
    ): {newRightElement: IMatchPairQuestion[]; newPairs: {leftElementId: string; rightElementId: string}[]} => {
        const existingRightElement = highlightedElement.rightElement.find((item) => item.text === value);
        const currentRightElementId = highlightedElement.pairs.find((pair) => pair.leftElementId === leftElementId)?.rightElementId;
        const isUsedElsewhere = highlightedElement.pairs.some(
            (pair) => pair.rightElementId === currentRightElementId && pair.leftElementId !== leftElementId
        );

        let updatedChangedAnswer: IMatchPairQuestion;
        let newRightElement: IMatchPairQuestion[];

        if (existingRightElement) {
            updatedChangedAnswer = {...changedAnswer, id: existingRightElement.id};
            newRightElement = elementToChange.map((item) => (item.id === elementToChange[index].id ? updatedChangedAnswer : item));
        } else {
            updatedChangedAnswer = handleNewRightElement(elementToChange, index, changedAnswer, value);
            newRightElement = applyNewRightElement(elementToChange, updatedChangedAnswer, currentRightElementId, isUsedElsewhere);
        }

        const newPairs = highlightedElement.pairs.map((pair) =>
            pair.leftElementId === leftElementId ? {...pair, rightElementId: updatedChangedAnswer.id} : pair
        );

        return {newRightElement, newPairs};
    };

    function elementAnswerChangeHandler(
        highlightedElementChangeHandler: {
            single: (field: keyof IElementQuestionMatchPair | string, value: unknown) => void;
            multiple: (fields: (keyof IElementQuestionMatchPair)[], values: unknown[]) => void;
        },
        index: number,
        value: string,
        elementToChange: IMatchPairQuestion[],
        keyOfElementToChange: keyof IElementQuestionMatchPair,
        leftElementId?: string
    ): void {
        const changedAnswer = {...elementToChange[index], text: value};

        if (keyOfElementToChange !== 'rightElement' || !leftElementId) {
            const newAnswers = swapArrayElement(elementToChange, index, changedAnswer);
            highlightedElementChangeHandler.single(keyOfElementToChange, newAnswers);
            return;
        }

        const updatedData = updateRightElement(elementToChange, index, changedAnswer, value, leftElementId);
        highlightedElementChangeHandler.multiple(['rightElement', 'pairs'], [updatedData.newRightElement, updatedData.newPairs]);
    }

    function removeElement(
        highlightedElement: IElementQuestionMatchPair,
        highlightedElementChangeHandler: {
            multiple: (fields: (keyof IElementQuestionMatchPair)[], values: unknown[]) => void;
        },
        index: number
    ): void {
        const elementToDelete = highlightedElement.pairs[index];
        const newPairs = removeArrayElement(highlightedElement.pairs, index);
        const newLeftElement = removeArrayElement(
            highlightedElement.leftElement,
            highlightedElement.leftElement.map((i) => i.id).indexOf(elementToDelete.leftElementId)
        );
        const newRightElement = removeArrayElement(
            highlightedElement.rightElement,
            highlightedElement.rightElement.map((i) => i.id).indexOf(elementToDelete.rightElementId)
        );
        highlightedElementChangeHandler.multiple(['leftElement', 'rightElement', 'pairs'], [newLeftElement, newRightElement, newPairs]);
    }

    return (
        <>
            {highlightedElement.pairs.map((pairsElement, index) => (
                <div className="u-mb-1" key={`accordion-${index}`}>
                    <AccordionItem
                        header={
                            <div
                                className={`u-flex u-h-between u-v-center u-mr-2 ${getErrorClasses(errors, `leftElement.${index}`)}`}
                                style={{width: '100%'}}
                            >
                                <div>
                                    {t('presEditPage.editSidebar.elements.matchPairQuestion.pair')} #{index + 1}
                                </div>
                                <Button
                                    variant="ghost"
                                    size="xs"
                                    square
                                    leading="solar:trash-bin-minimalistic-linear"
                                    disabled={isDeleteButtonDisabled(highlightedElement.pairs.length, 3)}
                                    onClick={() => removeElement(highlightedElement, highlightedElementChangeHandler, index)}
                                />
                            </div>
                        }
                    >
                        <div className="l-group--form">
                            <div className={`l-group__item ${getErrorClasses(errors, `leftElement.${index}.text`)}`}>
                                <Label>{t('presEditPage.editSidebar.elements.matchPairQuestion.leftElement')}</Label>
                                <DebouncedTextInput
                                    className="c-sidepanel__input"
                                    value={highlightedElement.leftElement.find((i) => i.id === pairsElement.leftElementId)?.text || ''}
                                    onChange={(value) =>
                                        elementAnswerChangeHandler(
                                            highlightedElementChangeHandler,
                                            highlightedElement.leftElement.map((i) => i.id).indexOf(pairsElement.leftElementId),
                                            value,
                                            highlightedElement.leftElement,
                                            'leftElement'
                                        )
                                    }
                                    validator={(val) => {
                                        const validation = pairElementValidator.shape.text.safeParse(val);
                                        return validation.success;
                                    }}
                                    maxLength={MAX_MATCH_PAIR_LENGTH}
                                />
                            </div>
                            <div className={`${getErrorClasses(errors, `rightElement.${index}.text`)}`}>
                                <Label>{t('presEditPage.editSidebar.elements.matchPairQuestion.rightElement')}</Label>
                                <DebouncedTextInput
                                    className="c-sidepanel__input"
                                    value={highlightedElement.rightElement.find((i) => i.id === pairsElement.rightElementId)?.text || ''}
                                    onChange={(value) =>
                                        elementAnswerChangeHandler(
                                            highlightedElementChangeHandler,
                                            highlightedElement.rightElement.map((i) => i.id).indexOf(pairsElement.rightElementId),
                                            value,
                                            highlightedElement.rightElement,
                                            'rightElement',
                                            pairsElement.leftElementId
                                        )
                                    }
                                    validator={(val) => {
                                        const validation = pairElementValidator.shape.text.safeParse(val);
                                        return validation.success;
                                    }}
                                    maxLength={MAX_MATCH_PAIR_LENGTH}
                                />
                            </div>
                        </div>
                    </AccordionItem>
                </div>
            ))}
        </>
    );
}

export default HighlightedPairsElement;
